import { calendarDate, dateTimeNoSeconds } from '../../shared/dateTime';
import { plNumber } from '../../shared/number';
import { plural } from '../../shared/plural';
import {
  UNKNOWN_STATUS_APPEARANCE,
  planningStatusAppearance,
  type DictionaryStatus,
} from './planningStatus';
import { tmsOrderStatusText } from './tmsOrderStatus';
import { STATUS_APPEARANCE, isKnownAcceptanceStatus } from './acceptanceStatus';
import { vehicleChangeAlertText } from '../fleet/carChanges';
import { viaWriteStatusLabel } from '../../shared/status';
import { snapshotDecisionText, snapshotSummary, validityLogText } from './recommendationSnapshot';

/**
 * Historia zmian zlecenia — nazwy pól i zapis wartości dla operatora.
 *
 * Dziennik (`UserOfferChange`) trzyma wartości jako tekst w zapisie
 * maszynowym: daty w ISO, `true`/`false`, kody stanów, id pojazdów. E2e na
 * dev (28.09.2026) pokazało je wprost („2026-09-01T00:00:00.000Z”,
 * „assigned → ready”, „1037”), a pola z importu TMS bez tłumaczenia.
 * Jedna mapa: pole → etykieta i sposób pokazania wartości.
 *
 * Nazwy pól to dane z dziennika (klucze zapisane w bazie) — nie zmieniamy
 * ich, tylko podpisujemy. Pole spoza mapy zostaje jak jest, wartość też:
 * nie zgadujemy, co znaczy.
 */

/** Czego formatter potrzebuje z otoczenia. */
export interface ChangeFormatContext {
  /** Słownik stanów z API — etykiety jak w banku. */
  states: ReadonlyArray<DictionaryStatus>;
  /** Numery rejestracyjne znane ze szczegółów zlecenia (id → numer). */
  vehicles: ReadonlyMap<number, string>;
  /**
   * Stan końcowy uwagi od FTL nazywa się „zakończona” (21.10.2026,
   * `ftlDecisionNotifications`). Brak = „załatwiona”, jak dotąd.
   */
  ftlNoteEnded?: boolean;
}

type Formatter = (value: string, ctx: ChangeFormatContext) => string;

const yesNo: Formatter = (v) => (v === 'true' ? 'tak' : v === 'false' ? 'nie' : v);

/** Chwila (okno zlecenia) — czas polski. */
const dateTime: Formatter = (v) => {
  const text = dateTimeNoSeconds(v);
  return text === '—' ? v : text;
};

/** Data bez godziny (termin ważności) — dzień z zapisu, bez „02:00”. */
const dateOnly: Formatter = (v) => {
  const text = calendarDate(v);
  return text === '—' ? v : text;
};

const planningStatus: Formatter = (v, ctx) => {
  const appearance = planningStatusAppearance(v, ctx.states);
  // Kod, którego nie zna ani słownik, ani front — pokazujemy wprost.
  return appearance === UNKNOWN_STATUS_APPEARANCE ? v : appearance.label;
};

const decimal =
  (digits: number): Formatter =>
  (v) => {
    const n = Number(v);
    return v.trim() !== '' && Number.isFinite(n) ? plNumber(n, digits) : v;
  };

/**
 * Pojazd zapisany w dzienniku jako id. Numer rejestracyjny bierzemy ze
 * szczegółów zlecenia (obsadzenie, kandydatury — także zwolnione). Id, którego
 * tam nie ma, zostaje podpisane jako id — numeru nie zgadujemy.
 */
const vehicle: Formatter = (v, ctx) => {
  if (!/^\d+$/.test(v)) return vehicleIdsInText(v, ctx);
  return ctx.vehicles.get(Number(v)) ?? `pojazd #${v}`;
};

/**
 * Stare wpisy z id auta w tekście: „pojazd 1161 kandydował też do: …”,
 * „pojazd 1161 należy do drugiej spółki” (weryfikacja na dev, 28.09.2026).
 * Tekst zapisano w chwili zdarzenia i go nie przepisujemy; przy wyświetlaniu
 * podmieniamy id na numer z mapy `vehicleNumbers` (API zbiera id z tego
 * samego wzorca — `vehicleIdsInChanges`). Nowe wpisy mają numer
 * w cudzysłowie albo `#id` i wzorca nie spełniają. Id spoza mapy zostaje
 * jak było — numeru nie zgadujemy.
 */
export const vehicleIdsInText: Formatter = (v, ctx) =>
  v.replace(/\bpojazd (\d+)(?![\w#])/g, (whole, id: string) => {
    const plate = ctx.vehicles.get(Number(id));
    return plate ? `pojazd „${plate}”` : whole;
  });

/**
 * Sygnatura alertu przeplanowania (np. `betterCar:2;late:delivery`) to klucz
 * techniczny — w historii pokazujemy zamiast niej „—”. Zdanie dla człowieka
 * ma spacje, więc wzorca nie spełnia i zostaje.
 */
const SIGNATURE = /^[A-Za-z]+(?::[\w.-]+)?(?:;[A-Za-z]+(?::[\w.-]+)?)*$/;
const hideSignature: Formatter = (v) => (SIGNATURE.test(v) ? '—' : v);

/**
 * Sygnatura wpisu „Anulowane w TMS” (API `via/tms-cancellations.ts`):
 * `<data ISO albo pusto>|<numer>|notify|quiet` — techniczna, w historii „—”.
 */
const tmsCancelledSignature: Formatter = (v) => (/^[^|]*\|.+\|(?:notify|quiet)$/.test(v) ? '—' : v);

/**
 * Sygnatura wpisów pętli zwrotnej z TMS (15.10.2026, API
 * `via/pre-order-link.ts`): `userOffer:<id>` — drugi wiersz (technicznie,
 * w historii „—”), `number:<nasz numer>` — numer, po którym połączył import.
 */
const linkSignature: Formatter = (v) => {
  if (/^userOffer:\d+$/.test(v)) return '—';
  const number = /^number:(.+)$/.exec(v);
  return number ? number[1] : v;
};

/**
 * Kody stanów zapisywane w dzienniku przez giełdy i zapytania do przewoźników
 * (`oldValue`; w bazie zostają) — na ekranie słowem. Zdanie dla człowieka
 * (`newValue`) w mapie nie jest, więc przechodzi bez zmian.
 */
const POSTING_STATE_LABELS: Record<string, string> = {
  pending: 'oczekująca',
  posted: 'wystawiona',
  failed: 'nieudana',
  withdrawn: 'wycofana',
  cancelled: 'anulowana',
};
/**
 * Rozstrzygnięcie wniosku o odpięcie (`unassignDecision`, omówienie
 * 01.10.2026) — `newValue` to status wniosku z API (dane, nie tłumaczymy
 * w bazie), na ekranie słowem.
 */
const UNASSIGN_DECISION_LABELS: Record<string, string> = {
  approved: 'zaakceptowany, pojazd odpięty',
  rejected: 'odrzucony, pojazd zostaje',
  withdrawn: 'wycofany przez wnioskującego',
  expired: 'nieaktualny — zlecenie zmieniło się inną drogą',
};
/**
 * Rozstrzygnięcia wniosków po wyjeździe auta (makieta back office v2, B6,
 * 21.10.2026) — o przekazanie do Trade i o zmianę pojazdu.
 */
const TRADE_TRANSFER_DECISION_LABELS: Record<string, string> = {
  ...UNASSIGN_DECISION_LABELS,
  approved: 'zaakceptowany, zlecenie przekazane do Trade',
  rejected: 'odrzucony, zlecenie zostaje we flocie',
};
const VEHICLE_CHANGE_DECISION_LABELS: Record<string, string> = {
  ...UNASSIGN_DECISION_LABELS,
  approved: 'zaakceptowany, pojazd zmieniony',
  rejected: 'odrzucony, pojazd zostaje',
};
/** Numer wniosku albo słowo rozstrzygnięcia — `oldValue` / `newValue` wpisu decyzji. */
const decisionFormat =
  (labels: Record<string, string>) =>
  (v: string): string =>
    /^\d+$/.test(v) ? `wniosek nr ${v}` : (labels[v] ?? v);
const INQUIRY_STATE_LABELS: Record<string, string> = {
  draft: 'szkic',
  sent_manually: 'wysłane ręcznie',
};
/**
 * Wniosek o wyjątek od bramki publikacji (makieta back office v2, fala B,
 * 21.10.2026): `publicationExceptionRequested` — `oldValue` = reguła klienta
 * (kod), `newValue` = numer wniosku; `publicationExceptionDecision` —
 * `oldValue` = numer wniosku, `newValue` = rozstrzygnięcie (kod). Powód we wpisie.
 */
const EXCEPTION_RULE_LABELS: Record<string, string> = {
  dozwolony: 'podwykonawca dozwolony',
  warunkowy: 'podwykonawca dozwolony warunkowo',
  zakaz: 'zakaz podwykonawstwa',
  nieustalona: 'reguła nieustalona',
};
const EXCEPTION_DECISION_LABELS: Record<string, string> = {
  approved: 'zatwierdzony — trader może publikować',
  rejected: 'odrzucony',
  withdrawn: 'wycofany przez wnioskującego',
  expired: 'nieaktualny',
};
/**
 * Stany „Uwag od FTL” (`ftlNoteStatus`, makieta back office v2, 20.10.2026)
 * — kody z bazy, na ekranie słowem jak na liście uwag.
 */
const FTL_NOTE_STATE_LABELS: Record<string, string> = {
  new: 'nowa',
  in_progress: 'w toku',
  resolved: 'załatwiona',
};
/**
 * Zdarzenia rejestru zmiany zlecenia (`orderChange`, makieta back office v2,
 * fala B, B9, 21.10.2026) — kody z API, na ekranie słowem.
 */
const ORDER_CHANGE_EVENT_LABELS: Record<string, string> = {
  requested: 'zgłoszona',
  approved_cs: 'zgoda CS',
  approved_execution: 'zgoda osoby realizującej',
  accepted: 'zaakceptowana — do wprowadzenia w TMS',
  rejected: 'odrzucona',
  withdrawn: 'wycofana',
  entered: 'wprowadzona w TMS (oświadczenie)',
  closed: 'zamknięta — TMS zgodny',
  reminder: 'przypomnienie: TMS pokazuje inną wartość',
  // Właściciel pola i uwaga osoby realizującej (21.10.2026, `orderChangeFieldOwner`).
  approved_administration: 'zgoda Administracji (właściciel pola)',
  consulted: 'uwaga osoby realizującej',
};
const codeLabel =
  (labels: Record<string, string>): Formatter =>
  (v) =>
    labels[v] ?? v;

/**
 * Stan na osi „przyjęcie → zapis w TMS” (`acceptanceStatus`, np. akceptacja
 * prezlecenia: `new` → `accepted`) — w historii szedł kodem (arkusz
 * „Problemy poza brzmieniem”, 07.10.2026). Etykiety jak na karcie
 * (`acceptanceStatus.ts`); nieznany kod zostaje — nie udajemy, że jest w TMS.
 */
const acceptanceStatus: Formatter = (v) =>
  isKnownAcceptanceStatus(v) ? STATUS_APPEARANCE[v].label : v;
/**
 * Źródło punktu z geokodowania (`here` / `tomtom` / `geonames` — zapasowy
 * słownik kodów, 19.10.2026) — nazwa dostawcy; GeoNames z licencją CC BY 4.0.
 */
const geoSource = codeLabel({
  here: 'HERE',
  tomtom: 'TomTom',
  geonames: 'słownik GeoNames, CC BY 4.0',
});
/**
 * Kontrahent w dzienniku (21.10.2026): id z kartoteki — numerem (nazwy
 * w dzienniku nie ma, nie zgadujemy jej); zdanie zostaje zdaniem.
 */
const contractorRef: Formatter = (v) => (/^\d+$/.test(v) ? `kontrahent #${v}` : v);

/** Źródło wiersza banku (`sourceSystem`) — przy połączeniu z TMS. */
const recordSource = codeLabel({
  'via-tms': 'TMS',
  prezlecenie: 'prezlecenie',
  'via-tms-polaczone': 'TMS, połączone z prezleceniem',
  demo: 'dane pokazowe',
});

export const CHANGE_FIELDS: Record<string, { label: string; format?: Formatter }> = {
  planningStatus: { label: 'Stan planowania', format: planningStatus },
  assignedCarId: { label: 'Obsadzenie pojazdem', format: vehicle },
  candidate: { label: 'Wariant pojazdu', format: vehicle },
  atRisk: { label: 'Zgłoszone ryzyko', format: yesNo },
  riskReason: { label: 'Powód ryzyka' },
  // Status oferty na pojeździe — API zapisuje gotowy opis „na stole · WGM1234”.
  vehicleOffer: { label: 'Oferta na pojeździe' },
  validUntil: { label: 'Termin ważności', format: dateOnly },
  // Termin ważności wariantu (20.10.2026, makieta back office v2): JSON
  // z autem i terminem (API `variant-validity.ts`) — na ekranie numer i termin.
  variantValidUntil: {
    label: 'Termin ważności wariantu',
    format: (v, ctx) => validityLogText(v, ctx.vehicles),
  },
  // Migawka rekomendacji z chwili decyzji (20.10.2026): `oldValue` — przy
  // czym, `newValue` — JSON migawki; szczegóły rozwija „Historia zmian”.
  recommendationSnapshot: {
    label: 'Rekomendacja z chwili decyzji',
    format: (v) => (v.startsWith('{') ? snapshotSummary(v) : snapshotDecisionText(v)),
  },
  ostrzezenie: { label: 'Ostrzeżenie', format: vehicleIdsInText },
  // `oldValue` = id pojazdu, `newValue` = numer w chwili odrzucenia.
  suggestionRejected: { label: 'Odrzucona rekomendacja pojazdu', format: vehicle },
  // Obsadzenie mimo blokady przez Team Leadera (16.10.2026, pyt. 54e):
  // `oldValue` = obejście blokady (zdania z odmowy), `newValue` = pojazd
  // tekstem; powód we wpisie.
  assignmentBlockOverride: { label: 'Obsadzenie mimo blokady uprawnień (Team Leader)' },
  // Obsadzenie mimo pewnego braku czasu (22.10.2026, decyzja użytkownika
  // pkt 3, API `variant-window.ts`): `newValue` — zdanie z obiema liczbami
  // spóźnienia; kto i kiedy — autor wpisu.
  assignedDespiteLate: { label: 'Obsadzono mimo braku czasu' },
  acceptanceStatus: { label: 'Stan zapisu do TMS', format: acceptanceStatus },
  // Akceptacja prezlecenia nadaje numer (`pre-order/manual-entry.service.ts`).
  externalId: { label: 'Numer zlecenia' },
  // Pętla zwrotna z TMS (15.10.2026): zlecenie z prezlecenia wróciło z TMS.
  sourceSystem: { label: 'Źródło zlecenia', format: recordSource },
  tmsLink: { label: 'Połączenie ze zleceniem z TMS', format: linkSignature },
  // Koperta „Jest w TMS” przy połączeniu z TMS (16.10.2026); od 21.10.2026
  // także zatwierdzenie, plik, ponowienie i „usunąłem w VIA” (powód we wpisie).
  viaWriteStatus: { label: 'Stan zapisu do VIA', format: (v) => viaWriteStatusLabel(v) },
  // „Dane dla importera”: okno i miejsce punktu było → jest (21.10.2026).
  writeDetailsStation: { label: 'Dane dla importera: punkt (okno i miejsce)' },
  preOrderLinkCandidate: { label: 'Możliwe połączenie z prezleceniem', format: linkSignature },
  tmsLinkCandidate: { label: 'Możliwe połączenie ze zleceniem z TMS', format: linkSignature },
  mergedIntoPreOrder: { label: 'Połączone z prezleceniem', format: linkSignature },
  // Kontrahent z kartoteki (fala B, 21.10.2026, API `pre-order-contractor.ts`):
  // wybór na prezleceniu i — przy połączeniu z TMS — kontrahent z TMS
  // (`contractorFromTms`: `oldValue` = id wybranego, `newValue` = zdanie).
  contractorId: { label: 'Kontrahent z kartoteki', format: contractorRef },
  contractorFromTms: { label: 'Kontrahent z TMS po połączeniu', format: contractorRef },
  // Połączenie z TMS mimo innego kontrahenta — znacznik i „sprawdziłem”
  // (21.10.2026, `preOrderLinkOtherContractor`; zdanie w `newValue`).
  contractorLinkCheck: { label: 'Połączono mimo innego kontrahenta — do sprawdzenia', format: linkSignature },
  contractorLinkChecked: { label: 'Kontrahent po połączeniu — sprawdzone' },
  // Awizacja i notatka CS przy zleceniu (21.10.2026, `csLight`): rodzaj
  // w `newValue`, kanał, wynik i notatka w powodzie wpisu.
  clientContact: { label: 'Kontakt z klientem (CS)', format: codeLabel({ avization: 'awizacja', note: 'notatka' }) },
  // Awizacje i aktualizacje dla klienta (26.10.2026, pakiet P1): zdarzenie
  // w `newValue`, opis w powodzie wpisu (bez danych kontaktowych).
  clientAvization: {
    label: 'Awizacja (CS)',
    format: codeLabel({ created: 'zadanie założone', skipped: 'pominięta', done: 'wykonana' }),
  },
  clientUpdate: {
    label: 'Aktualizacja dla klienta (CS)',
    format: codeLabel({ sent_manually: 'wysłana ręcznie', skipped: 'pominięta' }),
  },
  // Ostrzeżenie o duplikacie przed zapisem do VIA (16.10.2026): sygnatura
  // drugiego wiersza pary w `oldValue`, zdanie w `newValue`, powód operatora.
  notDuplicateConfirmed: { label: 'Potwierdzone: to nie duplikat', format: linkSignature },
  publikacja: { label: 'Publikacja na giełdzie', format: codeLabel(POSTING_STATE_LABELS) },
  // Pola notowane przez import z TMS (`via/import-changes.ts`).
  tmsOrderStatus: { label: 'Status w TMS', format: (v) => tmsOrderStatusText(v) },
  assignedVehicleNumber: { label: 'Pojazd w TMS' },
  tmsCarrierTitle: { label: 'Przewoźnik w TMS' },
  fromDate: { label: 'Załadunek od', format: dateTime },
  toDate: { label: 'Rozładunek do', format: dateTime },
  fromCity: { label: 'Miasto załadunku' },
  toCity: { label: 'Miasto rozładunku' },
  // Nagłówek z edycji prezlecenia (`pre-order/order-bank.ts`, 06.10.2026).
  fromCountry: { label: 'Kraj załadunku' },
  fromPostalCode: { label: 'Kod pocztowy załadunku' },
  toCountry: { label: 'Kraj rozładunku' },
  toPostalCode: { label: 'Kod pocztowy rozładunku' },
  // Numer zamówienia klienta i wymagany typ pojazdu (16.10.2026, odpowiedzi
  // Marathonu/Euvic, pyt. 27 i 39e) — z prezlecenia i z „Dane dla importera”.
  customerOrderNumber: { label: 'Nr zamówienia klienta' },
  assignedVehicleType: { label: 'Wymagany typ pojazdu' },
  // Punkt na mapie z geokodowania (06.10.2026, `geocoding/order-geo.ts`):
  // wartość to współrzędne, skąd — w powodzie wpisu.
  fromGeo: { label: 'Punkt załadunku na mapie' },
  toGeo: { label: 'Punkt rozładunku na mapie' },
  // Import: źródło punktu — `here`/`tomtom`/`geonames` z geokodowania, pusto = z TMS.
  fromGeoSource: { label: 'Punkt załadunku z geokodowania', format: geoSource },
  toGeoSource: { label: 'Punkt rozładunku z geokodowania', format: geoSource },
  price: { label: 'Cena', format: decimal(2) },
  currency: { label: 'Waluta' },
  fixed: { label: 'Stała godzina (zamiast okna)', format: yesNo },
  ldm: { label: 'LDM', format: decimal(2) },
  // Zmiany w TMS po obsadzeniu (`via/post-assignment-changes.ts`) — wartości
  // przychodzą gotowym tekstem, punkt jest w powodzie wpisu.
  keyPointAdded: { label: 'Dodany punkt trasy (TMS)' },
  keyPointRemoved: { label: 'Usunięty punkt trasy (TMS)' },
  keyPointPlace: { label: 'Miejsce punktu (TMS)' },
  keyPointWindow: { label: 'Termin punktu (TMS)' },
  keyPointWeight: { label: 'Waga punktu, kg (TMS)', format: decimal(0) },
  // Połączenie z prezleceniem (16.10.2026): waga z prezlecenia → suma
  // załadunków w TMS.
  preOrderWeight: { label: 'Waga ładunku, kg (prezlecenie → TMS)', format: decimal(0) },
  tmsChangeAfterAssignment: { label: 'Zmiana w TMS po obsadzeniu' },
  tmsCancellationSuspected: { label: 'Anulacja w TMS — do potwierdzenia' },
  // Lista anulowanych w TMS (odpowiedź Marathonu/Euvic 16.10.2026, pyt. 41;
  // przełącznik `tmsCancellations`): `oldValue` = sygnatura (data
  // modyfikacji, numer, dzwonek — „—” w historii), `newValue` = zdanie.
  tmsCancelled: { label: 'Anulowane w TMS', format: tmsCancelledSignature },
  // Zmiana pojazdu w TMS po obsadzeniu (12.10.2026): `newValue` to JSON
  // z importu kartoteki pojazdów — na ekranie to samo zdanie co w banerze
  // (`fleet/carChanges.ts`, lustro API).
  vehicleChangedAfterAssignment: {
    label: 'Zmiana pojazdu w TMS po obsadzeniu',
    format: (v) => vehicleChangeAlertText(v),
  },
  // Inny pojazd w TMS niż obsadzony u nas, gdy import nie poszedł za TMS
  // (decyzja użytkownika 12.10.2026): `oldValue` = sygnatura pary
  // (`car:501;tms:PO77777`, odsiew powtórek), `newValue` = zdanie.
  tmsVehicleMismatch: { label: 'Inny pojazd w TMS niż u nas', format: hideSignature },
  // Anulowanie przez CS i Administrację (25.10.2026, `intakeOrderCancellation`);
  // wartości to kody z API (`intake-cancellation.ts`), powód we wpisie.
  intakeCancellation: {
    label: 'Anulowanie w aplikacji (CS/Administracja)',
    format: (v) =>
      v === 'cancelled'
        ? 'anulowane w aplikacji — do usunięcia w TMS'
        : v === 'deleted_in_tms'
          ? 'usunięte w TMS (oświadczenie)'
          : v === 'tms_still_active'
            ? 'rozbieżność: TMS dalej podaje zlecenie'
            : v,
  },
  // Rozliczenie zlecenia (26.10.2026, P4b, `orderSettlement`): `newValue` to
  // zdanie z API (postój, korekta, przekazanie do fakturowania), powód we wpisie.
  settlement: { label: 'Rozliczenie (Administracja)' },
  // Decyzja planisty (30.09.2026); wartości to kody z API, powód we wpisie.
  tmsCancellationDecision: {
    label: 'Decyzja o anulacji w TMS',
    format: (v) =>
      v === 'confirmed'
        ? 'anulacja potwierdzona, pojazd zwolniony'
        : v === 'notCancellation'
          ? 'to nie anulacja, plan bez zmian'
          : v,
  },
  // `newValue` = id potwierdzonych wpisów po przecinku. To samo pole
  // potwierdza alerty zmian w TMS i rekomendacje przeplanowania
  // („Przyjmuję do wiadomości”); na ekranie liczba alertów z odmianą.
  tmsChangeAcknowledged: {
    label: 'Alerty przyjęte do wiadomości',
    format: (v) => plural(v.split(',').length, 'alert', 'alerty', 'alertów'),
  },
  // `oldValue` = sygnatura wyzwalaczy (do odsiewu powtórek), `newValue` =
  // zdanie dla człowieka (omówienie 01.10.2026: sygnatury nie pokazujemy).
  replanSuggested: { label: 'Rekomendowane przeplanowanie', format: hideSignature },
  // Alert przed załadunkiem (Marathon 07.10.2026): `oldValue` = dzień
  // załadunku (sygnatura do odsiewu powtórek), `newValue` = zdanie.
  loadingTomorrowUnassigned: { label: 'Jutro załadunek, bez auta', format: dateOnly },
  carrierInquiry: { label: 'Zapytanie do przewoźnika', format: codeLabel(INQUIRY_STATE_LABELS) },
  // „Uwagi od FTL” (makieta back office v2, 20.10.2026): `newValue` =
  // kategoria (i „pilna”), treść w powodzie; zmiana stanu — kody stanów,
  // komentarz odbiorcy w powodzie.
  ftlNote: { label: 'Uwaga dla CS' },
  // Rejestr zmiany zlecenia (fala B, B9): `oldValue` = numer i pole zmiany,
  // `newValue` = zdarzenie, opis w powodzie.
  orderChange: { label: 'Zmiana zlecenia (było → ma być)', format: codeLabel(ORDER_CHANGE_EVENT_LABELS) },
  ftlNoteStatus: {
    label: 'Stan uwagi dla CS',
    format: (v, ctx) =>
      v === 'resolved' && ctx.ftlNoteEnded ? 'zakończona' : codeLabel(FTL_NOTE_STATE_LABELS)(v, ctx),
  },
  // Wniosek o odpięcie (omówienie 01.10.2026): `oldValue` = pojazd w tekście,
  // `newValue` = stan, do którego zlecenie wróci; powód we wpisie.
  unassignRequested: { label: 'Wniosek o odpięcie pojazdu', format: planningStatus },
  publicationExceptionRequested: {
    label: 'Wniosek o wyjątek od bramki publikacji',
    format: (v) => (/^\d+$/.test(v) ? `wniosek nr ${v}` : (EXCEPTION_RULE_LABELS[v] ?? v)),
  },
  publicationExceptionDecision: {
    label: 'Decyzja o wyjątku od bramki publikacji',
    format: (v) => (/^\d+$/.test(v) ? `wniosek nr ${v}` : (EXCEPTION_DECISION_LABELS[v] ?? v)),
  },
  // `oldValue` = numer wniosku, `newValue` = rozstrzygnięcie.
  unassignDecision: {
    label: 'Decyzja o odpięciu pojazdu',
    format: decisionFormat(UNASSIGN_DECISION_LABELS),
  },
  // Wnioski po wyjeździe auta (makieta back office v2, B6, 21.10.2026):
  // przekazanie do Trade — `oldValue` = pojazd, `newValue` = stan docelowy;
  // zmiana pojazdu — `oldValue` = obecny pojazd, `newValue` = proponowany.
  tradeTransferRequested: { label: 'Wniosek o przekazanie do Trade', format: planningStatus },
  tradeTransferDecision: {
    label: 'Decyzja o przekazaniu do Trade',
    format: decisionFormat(TRADE_TRANSFER_DECISION_LABELS),
  },
  vehicleChangeRequested: { label: 'Wniosek o zmianę pojazdu' },
  // Rejestr ofert przewoźników (fala A, A9): `newValue` = rodzaj, przewoźnik,
  // kwota i źródło tekstem; odrzucenie — „odrzucona: …”, powód we wpisie.
  // Etykieta od 21.10.2026 (analiza śladu akcji, luka L12 — było „carrierOffer”).
  carrierOffer: { label: 'Oferta przewoźnika (rejestr ofert)' },
  // Akceptacja przewoźnika i stawki przez tradera (paczka p. Mariusza 21.10,
  // S-21.10-17): `newValue` = przewoźnik, stawka, pasmo i warunki tekstem
  // (cofnięcie — „akceptacja cofnięta”, poprzednia w `oldValue`);
  // uzasadnienie albo powód cofnięcia we wpisie.
  carrierAccepted: { label: 'Akceptacja przewoźnika' },
  // Czynność po obsadzeniu (24.10.2026, `orderChecklist`): `todo` / `done`,
  // nazwa czynności w powodzie — jak w makiecie FTL.
  postAssignmentTask: {
    label: 'Czynność po obsadzeniu',
    format: (v) => (v === 'done' ? 'wykonane' : v === 'todo' ? 'do zrobienia' : v),
  },
  // Akceptacja mimo niepełnej albo nieustalonej weryfikacji przewoźnika
  // (24.10.2026, S-24.10-11): `newValue` = „akceptacja mimo niepełnej
  // weryfikacji: …” z brakami, uzasadnienie tradera we wpisie.
  carrierVerificationOverride: { label: 'Weryfikacja przewoźnika — wyjątek' },
  vehicleChangeDecision: {
    label: 'Decyzja o zmianie pojazdu',
    format: decisionFormat(VEHICLE_CHANGE_DECISION_LABELS),
  },
};

export function changeFieldLabel(field: string): string {
  return CHANGE_FIELDS[field]?.label ?? field;
}

/** Wartość przed/po do pokazania; brak = „—”. */
export function changeValueText(field: string, value: unknown, ctx: ChangeFormatContext): string {
  if (value === null || value === undefined || value === '') return '—';
  const text = String(value);
  const format = CHANGE_FIELDS[field]?.format;
  return format ? format(text, ctx) : text;
}

/**
 * Numery rejestracyjne znane ze szczegółów zlecenia: mapa `vehicleNumbers`
 * z API (pojazdy z dziennika, także już niezwiązane ze zleceniem), obsadzony
 * pojazd i pojazdy z kandydatur (API dociąga też zwolnione).
 */
export function knownVehicles(record: Record<string, unknown> | undefined): Map<number, string> {
  const out = new Map<number, string>();
  const add = (car: unknown) => {
    if (!car || typeof car !== 'object') return;
    const c = car as { id?: unknown; registrationNumber?: unknown; name?: unknown };
    const id = Number(c.id);
    const plate =
      typeof c.registrationNumber === 'string' && c.registrationNumber.trim() !== ''
        ? c.registrationNumber
        : typeof c.name === 'string' && c.name.trim() !== ''
          ? c.name
          : null;
    if (Number.isInteger(id) && plate) out.set(id, plate);
  };
  if (!record) return out;
  const numbers = record.vehicleNumbers;
  if (numbers && typeof numbers === 'object') {
    for (const [id, plate] of Object.entries(numbers as Record<string, unknown>)) {
      if (/^\d+$/.test(id) && typeof plate === 'string' && plate.trim() !== '') out.set(Number(id), plate);
    }
  }
  add(record.assignedCar);
  const candidates = Array.isArray(record.candidates) ? record.candidates : [];
  for (const k of candidates) add((k as { car?: unknown }).car);
  return out;
}
